import React from 'react';
import { Clock, Calendar, GraduationCap, BookOpen } from 'lucide-react';

const TimetableSection = () => {
  const foundationSlots = [
    { time: '9:00 AM - 10:00 AM' },
    { time: '11:00 AM - 12:00 PM' },
    { time: '1:00 PM - 2:00 PM' },
    { time: '3:00 PM - 4:00 PM' },
    { time: '5:00 PM - 6:00 PM' }
  ];

  const higherSlots = [
    { time: '10:00 AM - 11:00 AM' },
    { time: '12:00 PM - 1:00 PM' },
    { time: '2:00 PM - 3:00 PM' },
    { time: '4:00 PM - 5:00 PM' },
    { time: '6:00 PM - 7:00 PM' }
  ];

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <section id="timetable" style={{ padding: '5rem 1.5rem', background: 'var(--bg-section)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'var(--accent-wash)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Calendar size={28} color="var(--accent-text)" />
            </div>
          </div>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Class Timetable
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            All classes run <strong>Monday to Sunday</strong>. Choose a time slot that works best for your child's schedule.
          </p>
        </div>

        {/* Days Available Banner */}
        <div style={{
          maxWidth: '800px',
          margin: '0 auto 2.5rem',
          padding: '1rem 1.5rem',
          background: 'var(--accent-wash)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'center', 
            gap: '0.75rem',
            marginBottom: '0.5rem'
          }}>
            {days.map((day) => (
              <span 
                key={day}
                style={{
                  background: 'var(--accent-primary)',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                {day}
              </span>
            ))}
          </div>
          <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Classes available 7 days a week
          </p>
        </div>

        {/* Timetable Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '2rem',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {/* Foundation Level Card */}
          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            border: '3px solid #0F172A'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #0F172A 100%)',
              padding: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <BookOpen size={30} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Foundation Level
              </h3>
              <p style={{ opacity: 0.9, fontSize: '0.9rem', margin: 0 }}>
                GCSE Maths Grades 1-5
              </p>
            </div>

            {/* Time Slots */}
            <div style={{ padding: '1.5rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '1rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                <Clock size={18} />
                Available Time Slots:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {foundationSlots.map((slot, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.875rem 1rem',
                      background: 'rgba(59, 130, 246, 0.08)',
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    <span style={{ 
                      fontWeight: 600, 
                      color: '#0F172A',
                      fontSize: '1rem'
                    }}>
                      {slot.time}
                    </span>
                    <span style={{
                      background: '#0F172A',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      Available
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Higher Level Card */}
          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            border: '3px solid var(--accent-primary)'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, var(--accent-primary) 0%, #C5A059 100%)',
              padding: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <GraduationCap size={30} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Higher Level
              </h3>
              <p style={{ opacity: 0.9, fontSize: '0.9rem', margin: 0 }}>
                GCSE Maths Grades 4-9
              </p>
            </div>

            {/* Time Slots */}
            <div style={{ padding: '1.5rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '1rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                <Clock size={18} />
                Available Time Slots:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {higherSlots.map((slot, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.875rem 1rem',
                      background: 'rgba(64, 224, 208, 0.08)',
                      borderRadius: '8px',
                      border: '1px solid rgba(64, 224, 208, 0.3)'
                    }}
                  >
                    <span style={{ 
                      fontWeight: 600, 
                      color: 'var(--accent-text)',
                      fontSize: '1rem'
                    }}>
                      {slot.time}
                    </span>
                    <span style={{
                      background: 'var(--accent-primary)',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      Available
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '3rem'
        }}>
          <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            Can't find a suitable time? Contact us to discuss alternative arrangements.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary"
            >
              Book Your Slot
            </button>
            <a 
              href="https://calendly.com/drmomakobia/studentcall"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
              style={{ textDecoration: 'none' }}
            >
              Schedule Free Call
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimetableSection;
